
import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import Header from '../../../components/feature/Header'
import Footer from '../../../components/feature/Footer'
import BookingModal from '../../../components/feature/BookingModal'
import ReviewsSection from '../../../components/feature/ReviewsSection'

interface Coach {
  id: string
  specialty: string
  experience_years: number
  hourly_rate: number
  location: string
  description: string
  languages: string[]
  rating: number
  total_reviews: number
  certifications: string[]
  availability: any
  users: {
    first_name: string
    last_name: string
    avatar_url: string
  }
}

export default function ServiceDetail() {
  const [searchParams] = useSearchParams()
  const coachId = searchParams.get('id')
  const [coach, setCoach] = useState<Coach | null>(null)
  const [loading, setLoading] = useState(true)
  const [showBookingModal, setShowBookingModal] = useState(false)

  useEffect(() => {
    if (coachId) {
      fetchCoachDetails()
    }
  }, [coachId])

  const fetchCoachDetails = async () => {
    try {
      // Simuler les détails du coach (en attendant une API dédiée)
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/get-coaches`)
      const data = await response.json()
      
      if (data.coaches) {
        const foundCoach = data.coaches.find((c: Coach) => c.id === coachId)
        if (foundCoach) {
          setCoach(foundCoach)
        }
      }
    } catch (error) {
      console.error('Erreur lors du chargement des détails du coach:', error)
    } finally {
      setLoading(false)
    }
  }

  const renderStars = (rating: number) => {
    return (
      <div className="flex items-center">
        {[1, 2, 3, 4, 5].map((star) => (
          <i
            key={star}
            className={`ri-star-${star <= rating ? 'fill' : 'line'} text-yellow-400 text-sm`}
          ></i>
        ))}
      </div>
    )
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/3 mb-6"></div>
            <div className="grid lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2 space-y-6">
                <div className="h-64 bg-gray-200 rounded-lg"></div>
                <div className="h-32 bg-gray-200 rounded-lg"></div>
              </div>
              <div className="h-96 bg-gray-200 rounded-lg"></div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    )
  }

  if (!coach) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Coach non trouvé</h1>
            <p className="text-gray-600">Le coach que vous recherchez n'existe pas ou n'est plus disponible.</p>
          </div>
        </div>
        <Footer />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="container mx-auto px-4 py-8">
        {/* Breadcrumb */}
        <nav className="text-sm text-gray-600 mb-6">
          <a href="/services" className="hover:text-blue-600">Services</a>
          <span className="mx-2">›</span>
          <span className="text-gray-900">{coach.users.first_name} {coach.users.last_name}</span>
        </nav>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Contenu principal */}
          <div className="lg:col-span-2 space-y-6">
            {/* Profil du coach */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <div className="flex items-start space-x-6">
                <img
                  src={coach.users.avatar_url}
                  alt={`${coach.users.first_name} ${coach.users.last_name}`}
                  className="w-24 h-24 rounded-full object-cover"
                />
                <div className="flex-1">
                  <h1 className="text-2xl font-bold text-gray-900 mb-2">
                    {coach.users.first_name} {coach.users.last_name}
                  </h1>
                  <p className="text-lg text-blue-600 font-medium mb-2">{coach.specialty}</p>
                  
                  <div className="flex items-center space-x-4 text-sm text-gray-600 mb-3">
                    <div className="flex items-center">
                      {renderStars(Math.round(coach.rating))}
                      <span className="ml-2">{coach.rating.toFixed(1)} ({coach.total_reviews} avis)</span>
                    </div>
                    <div className="flex items-center">
                      <i className="ri-map-pin-line mr-1"></i>
                      {coach.location}
                    </div>
                    <div className="flex items-center">
                      <i className="ri-time-line mr-1"></i>
                      {coach.experience_years} ans d'expérience
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {coach.languages?.map((lang, index) => (
                      <span key={index} className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs">
                        {lang}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Description */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">À propos</h2>
              <p className="text-gray-700 leading-relaxed">{coach.description}</p>
            </div>

            {/* Certifications */}
            {coach.certifications && coach.certifications.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm border p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Certifications</h2>
                <div className="grid md:grid-cols-2 gap-3">
                  {coach.certifications.map((cert, index) => (
                    <div key={index} className="flex items-center p-3 bg-green-50 rounded-lg">
                      <i className="ri-award-line text-green-600 mr-3"></i>
                      <span className="text-gray-800 text-sm">{cert}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Section des avis */}
            <ReviewsSection coachId={coach.id} />
          </div>

          {/* Sidebar de réservation */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm border p-6 sticky top-6">
              <div className="text-center mb-6">
                <div className="text-3xl font-bold text-gray-900 mb-1">
                  {coach.hourly_rate}€
                </div>
                <div className="text-gray-600">par séance</div>
              </div>

              <div className="space-y-4 mb-6">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Durée</span>
                  <span className="font-medium">60 minutes</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Type</span>
                  <span className="font-medium">Séance individuelle</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Disponibilité</span>
                  <span className="font-medium text-green-600">Disponible</span>
                </div>
              </div>

              <button
                onClick={() => setShowBookingModal(true)}
                className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors whitespace-nowrap"
              >
                Réserver une séance
              </button>

              <div className="mt-4 text-center">
                <button className="text-blue-600 hover:text-blue-700 text-sm font-medium whitespace-nowrap">
                  <i className="ri-message-3-line mr-1"></i>
                  Contacter le coach
                </button>
              </div>

              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="flex items-center text-sm text-gray-600 mb-2">
                  <i className="ri-shield-check-line text-green-500 mr-2"></i>
                  Coach vérifié
                </div>
                <div className="flex items-center text-sm text-gray-600 mb-2">
                  <i className="ri-time-line text-green-500 mr-2"></i>
                  Répond généralement en 2h
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <i className="ri-refresh-line text-green-500 mr-2"></i>
                  Annulation gratuite 24h avant
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />

      {/* Modal de réservation */}
      <BookingModal
        isOpen={showBookingModal}
        onClose={() => setShowBookingModal(false)}
        coach={coach}
      />
    </div>
  )
}
